<script lang="ts">
  import { addRoute } from '@integro/core';
  import { gettext, pgettext, ngettext, npgettext } from '@integro/gettext';
  addRoute({
    name: 'settings.statData',
    path: 'external-stat-data',
    component: 'StatData',
  });
</script>

<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'StatData',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    modal: {
      type: Boolean,
      required: false,
      default: false,
    },
    modalName: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const statDataCategories = ref([
    {
      id: 'exchange_rate',
      text: $fn.value.pgettext('StatDataCategory', 'Exchange rate'),
    },
    {
      id: 'base_rate',
      text: $fn.value.pgettext('StatDataCategory', 'Base rate'),
    },
    {
      id: 'inflation_index',
      text: $fn.value.pgettext('StatDataCategory', 'Inflation index'),
    },
  ]);
  const items = ref(null);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'sm',
  }));
  const b_3343864469950044_columns = computed(() => [
    {
      name: 'category',
      type: 'select',
      header: $fn.value.pgettext('StatData', 'Category'),
      width: '280px',
      required: true,
      options: statDataCategories.value,
    },
    {
      name: 'name',
      header: $fn.value.pgettext('StatData', 'Name'),
      width: '120px',
      required: true,
    },
    {
      name: 'source',
      header: $fn.value.pgettext('StatData', 'Source'),
      width: '120px',
    },
    {
      name: 'date',
      type: 'date',
      header: $fn.value.pgettext('StatData', 'Date'),
      width: '120px',
      required: true,
    },
    {
      name: 'value',
      type: 'number',
      header: $fn.value.gettext('Value'),
      width: '120px',
      required: true,
    },
    {
      name: 'unit',
      header: $fn.value.gettext('Unit'),
      width: '120px',
      required: true,
    },
  ]);

  const $data = computed(() => ({
    statDataCategories: statDataCategories.value,
    items: items.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
    b_3343864469950044_columns: b_3343864469950044_columns.value,
  }));

  const $$load_items = async () => {
    return $fn.value.fetch('list_stat_data').then((p) => p.data);
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'statDataCategories' },
      { name: 'items' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
      { name: 'b_3343864469950044_columns', nodeId: '3343864469950044' },
    ],
    loaders: { items: $$load_items },
    ux: {},
    dataRefs: {
      statDataCategories,
      items,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
      b_3343864469950044_columns,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    return null;
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.modal,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modal');
    }
  );
  watch(
    () => props.modalName,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modalName');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_vkn8bxiug_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_3343864469950044_script_ep2c4t6gs_value_callable = async (
    $event
  ) => {
    return $event.data.waitFor(
      $fn.value.crud('stat_data').upsert({
        ...$event.data.row,
        start: $event.data.row.lastvalue,
      })
    );
  };
  const act_3343864469950044_script_ep2c4t6gs_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_3343864469950044_script_ep2c4t6gs_value_callable($event);
      },
    };
  };
  const act_3343864469950044_script_oh8hhihqp_value_callable = async (
    $event
  ) => {
    return $event.data.waitFor(
      $fn.value.crud('stat_data').delete($event.data.row)
    );
  };
  const act_3343864469950044_script_oh8hhihqp_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_3343864469950044_script_oh8hhihqp_value_callable($event);
      },
    };
  };

  const act_7315092382398562_closeModal_vkn8bxiug =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_vkn8bxiug_getActionArgs,
    });
  const inst_evh_7315092382398562_close_rpba1xqym =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_vkn8bxiug,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_rpba1xqym],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_el9t03bag =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_hcvggx2hp =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_el9t03bag,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_hcvggx2hp],
    event: 'reload',
  });
  const act_8320016629450276_reloadComponentData_1dm8j7ips =
    new actionTypes.ReloadComponentDataAction({
      displayName: 'reload',
    });
  const inst_evh_8320016629450276_clickPreventStop_euu6votu4 =
    new actionTypes.EventHandlerImpl({
      action: act_8320016629450276_reloadComponentData_1dm8j7ips,
      event: 'click.prevent.stop',
      displayName: 'reload',
    });
  const inst_evh_8320016629450276_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_8320016629450276_clickPreventStop_euu6votu4],
      event: 'click.prevent.stop',
    });
  const act_3343864469950044_script_ep2c4t6gs = new actionTypes.ScriptAction({
    actionArgs: act_3343864469950044_script_ep2c4t6gs_getActionArgs,
  });
  const inst_evh_3343864469950044_save_jv3791dwv =
    new actionTypes.EventHandlerImpl({
      action: act_3343864469950044_script_ep2c4t6gs,
      event: 'save',
      displayName: 'script',
    });
  const inst_evh_3343864469950044_save = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_3343864469950044_save_jv3791dwv],
    event: 'save',
  });
  const act_3343864469950044_script_oh8hhihqp = new actionTypes.ScriptAction({
    actionArgs: act_3343864469950044_script_oh8hhihqp_getActionArgs,
  });
  const inst_evh_3343864469950044_delete_1vnkoag7t =
    new actionTypes.EventHandlerImpl({
      action: act_3343864469950044_script_oh8hhihqp,
      event: 'delete',
      displayName: 'script',
    });
  const inst_evh_3343864469950044_delete = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_3343864469950044_delete_1vnkoag7t],
    event: 'delete',
  });

  const actions = {
    inst_evh_7315092382398562_close_rpba1xqym,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_hcvggx2hp,
    inst_evh_7315092382398562_reload,
    inst_evh_8320016629450276_clickPreventStop_euu6votu4,
    reload: inst_evh_8320016629450276_clickPreventStop_euu6votu4,
    inst_evh_8320016629450276_clickPreventStop,
    inst_evh_3343864469950044_save_jv3791dwv,
    inst_evh_3343864469950044_save,
    inst_evh_3343864469950044_delete_1vnkoag7t,
    inst_evh_3343864469950044_delete,
  };

  const evh_7315092382398562_close_rpba1xqym = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_rpba1xqym.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_hcvggx2hp = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_hcvggx2hp.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8320016629450276_clickPreventStop_euu6votu4 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_8320016629450276_clickPreventStop_euu6votu4.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8320016629450276_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_8320016629450276_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3343864469950044_save_jv3791dwv = async (event, scope) => {
    return await actions.inst_evh_3343864469950044_save_jv3791dwv.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3343864469950044_save = async (event, scope) => {
    return await actions.inst_evh_3343864469950044_save.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3343864469950044_delete_1vnkoag7t = async (event, scope) => {
    return await actions.inst_evh_3343864469950044_delete_1vnkoag7t.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3343864469950044_delete = async (event, scope) => {
    return await actions.inst_evh_3343864469950044_delete.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="StatData"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <div class="overflow-hidden d-flex flex-column h-100 p-3">
      <ItpBox alignItems="center" :gap="2" class="mb-3">
        <strong>
          {{ gettext('External stat data') }}
        </strong>
        <ItpButton
          icon="fa-sync-alt"
          :text="gettext('Reload')"
          variant="light"
          size="sm"
          :spinning="actions.reload.isRunning"
          @click.prevent.stop="
            evh_8320016629450276_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </ItpBox>
      <ItpDataTable
        tableId="Settings.StatData"
        editable
        :isRowEditable="(row) => !row.locked"
        :value="
          _.orderBy(
            items,
            ['year', 'month', 'created_at'],
            ['desc', 'desc', 'desc']
          )
        "
        :rowTemplate="
          schemaDefaults('CreateStatData', {
            data_type: 'ksh_domestic_industrial_sales_price_index',
            year: new Date().getFullYear(),
            month: new Date().getMonth(),
          })
        "
        :columns="b_3343864469950044_columns"
        class="itp-datatable--flex-fill"
        v-if="loaded"
        @save="evh_3343864469950044_save($event, {})"
        @delete="evh_3343864469950044_delete($event, {})"
      >
      </ItpDataTable>
    </div>
  </ItpPage>
</template>
